<template>
  <div id="data-insights-con">
    <template v-if="isLoading">
      <div class="page-skeleton">
        <div class="skeleton-container">
          <div class="skeleton-box large">
            <div class="skeleton-text">
              Loading...
            </div>
          </div>
          <div class="skeleton-box small" />
        </div>
      </div>
    </template>
    <template v-else>
      <div class="left-area">
        <b-card>
          <div
            v-if="!canShowTableResults"
            class="info-text-con"
          >
            <h3 class="text-muted">
              {{
                T(
                  "Web.DataInsightsPage.ChooseRowsAndValuesToDisplay",
                  "Choose rows and values to display"
                )
              }}
            </h3>
          </div>
          <template v-else>
            <b-overlay
              v-if="tableIsBuilding"
              :show="true"
              style="margin-top: 30px"
            >
              <div style="height: 100%" />
            </b-overlay>
            <template v-else>
              <data-insight-table
                :table-data="tableData"
                :selected-values-options="selectedValuesOptions"
                :date-range="localUserSettings.dateRange"
                :sort-option="sortOption"
                @sort-option-changed="
                  (option) => (localUserSettings.sortOption = option)
                "
              />
            </template>
          </template>
        </b-card>
      </div>
      <div class="right-area">
        <b-card no-body>
          <div class="data-insights-brand-con">
            <span
              class="table-logo"
            >Data Insights <feather-icon
              size="20"
              icon="DatabaseIcon"
            /></span>
          </div>
          <div class="content-inner">
            <collapsable-menu-item
              :menu-title="T('Web.DataInsightsPage.Periode', 'Periode')"
              icon="CalendarIcon"
            >
              <date-range-picker
                ref="picker"
                v-model="localUserSettings.dateRange"
                opens="left"
                :locale-data="{ firstDay: 1, format: 'dd/mm/yyyy' }"
                :append-to-body="true"
                @update="handleDateRangeChange"
              />
            </collapsable-menu-item>
            <collapsable-menu-item
              :menu-title="T('Web.DataInsightsPage.Columns', 'Columns')"
              icon="ArrowDownIcon"
            >
              <b-form-radio
                v-for="option in visibleColumnOptions"
                :key="option.key"
                v-model="localUserSettings.selectedColumnOption"
                name="columnOption"
                :value="option.key"
                @change="refreshTable"
              >{{ option.label }}</b-form-radio>
              <div class="editOptionsBtn">
                <a
                  href="#"
                  @click.prevent="openColumnOptionsVisibilityEditModal"
                >
                  {{ T("Web.DataInsightsPage.EditOptions", "Edit options") }}
                </a>
              </div>
            </collapsable-menu-item>
            <collapsable-menu-item
              :menu-title="T('Web.DataInsightsPage.Rows', 'Rows')"
              :default-uncollapsed="true"
              icon="ArrowRightIcon"
            >
              <b-form-checkbox
                v-for="option in visibleRowOptions"
                :key="option.key"
                v-model="localUserSettings.selectedRowOptions"
                name="rowOption"
                :value="option.key"
                @change="refreshTable"
              >{{ option.label }}</b-form-checkbox>
              <div class="editOptionsBtn">
                <a
                  href="#"
                  @click.prevent="openRowOptionsVisibilityEditModal"
                >
                  {{ T("Web.DataInsightsPage.EditOptions", "Edit options") }}
                </a>
              </div>
              <div
                v-if="selectedRowOptions.length > 1"
                class="order-editor-con"
              >
                <label>{{ T("Web.DataInsightsPage.Order", "Order") }}</label>
                <div class="order-editor">
                  <draggable
                    v-model="localUserSettings.selectedRowOptions"
                    group="people"
                    @change="refreshTable"
                  >
                    <div
                      v-for="option in selectedRowOptions"
                      :key="option.key"
                    >
                      {{ option.label }}
                      <div class="icon-controls">
                        <feather-icon icon="MoreVerticalIcon" />
                      </div>
                    </div>
                  </draggable>
                </div>
              </div>
            </collapsable-menu-item>
            <collapsable-menu-item
              :menu-title="T('Web.DataInsightsPage.Values', 'Values')"
              :default-uncollapsed="true"
              icon="DatabaseIcon"
            >
              <b-form-checkbox
                v-for="option in visibleValuesOptions"
                :key="option.key"
                v-model="localUserSettings.selectedValuesOptions"
                name="valuesOptions"
                :value="option.key"
                @change="refreshTable"
              >{{ option.label }}</b-form-checkbox>
              <div class="editOptionsBtn">
                <a
                  href="#"
                  @click.prevent="openValueOptionsVisibilityEditModal"
                >
                  {{ T("Web.DataInsightsPage.EditOptions", "Edit options") }}
                </a>
              </div>
              <div
                v-if="selectedValuesOptions.length > 1"
                class="order-editor-con"
              >
                <label>{{ T("Web.DataInsightsPage.Order", "Order") }}</label>
                <div class="order-editor">
                  <draggable
                    v-model="localUserSettings.selectedValuesOptions"
                    group="people"
                    @change="refreshTable"
                  >
                    <div
                      v-for="option in selectedValuesOptions"
                      :key="option.key"
                    >
                      {{ option.label }}
                      <div class="icon-controls">
                        <feather-icon icon="MoreVerticalIcon" />
                      </div>
                    </div>
                  </draggable>
                </div>
              </div>
            </collapsable-menu-item>
            <collapsable-menu-item
              menu-title="Filterering"
              icon="FilterIcon"
            >
              <div
                v-for="option in visibleFilterOptions"
                :key="option.key"
                class="filter-editor-con"
              >
                <div class="filter-head">
                  <label>{{ option.label }}</label>
                  <a
                    href="#"
                    @click.prevent="selectDeselectAllFilterOption(option.key)"
                  >{{ T("Web.Generic.SelectDeselectAll") }}</a>
                </div>
                <div class="filter-editor">
                  <div
                    v-for="filterOption in option.value"
                    :key="filterOption.key"
                  >
                    <b-form-checkbox
                      v-model="localUserSettings.filters[option.key]"
                      :value="filterOption.id"
                      @change="refreshTable"
                    >{{ filterOption.name }}</b-form-checkbox>
                  </div>
                </div>
              </div>
            </collapsable-menu-item>
            <b-button
              variant="outline-secondary"
              class="mb-2 w-100"
              @click="exportToExcel"
            >
              {{ T("Web.Generic.Download", "Export to Excel") }} som Excel
              <feather-icon icon="DownloadIcon" />
            </b-button>
          </div>

          <edit-visible-options-modal
            ref="columnOptionsVisibilityEditModal"
            v-model="localUserSettings.visibleOptions.columnOptions"
            :options="columnOptions"
            :disabled-options="localUserSettings.selectedColumnOption"
          />
          <edit-visible-options-modal
            ref="valueOptionsVisibilityEditModal"
            v-model="localUserSettings.visibleOptions.valuesOptions"
            :options="valuesOptions"
            :disabled-options="localUserSettings.selectedValuesOptions"
          />
          <edit-visible-options-modal
            ref="rowOptionsVisibilityEditModal"
            v-model="localUserSettings.visibleOptions.rowOptions"
            :options="rowOptions"
            :disabled-options="localUserSettings.selectedRowOptions"
          />
        </b-card>
      </div>
    </template>
  </div>
</template>

<script>
import PageLoader from '@/app/components/layout/PageLoader.vue'
import ResourceService from '@/services/base/resource.service'
import {
  BButton,
  BCard,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BOverlay,
} from 'bootstrap-vue'
import moment from 'moment'
import { uuid } from 'vue-uuid'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import draggable from 'vuedraggable'
import * as XLSX from 'xlsx'
import DataInsightTable from './components/DataInsightTable.vue'
import EditVisibleOptionsModal from './components/EditVisibleOptionsModal.vue'
import CollapsableMenuItem from './components/CollapsableMenuItem.vue'

export default {
  components: {
    BCard,
    BFormRadio,
    BFormGroup,
    BFormCheckbox,
    DataInsightTable,
    CollapsableMenuItem,
    DateRangePicker,
    draggable,
    BFormInput,
    BButton,
    EditVisibleOptionsModal,
    PageLoader,
    BOverlay,
  },
  data() {
    return {
      tableIsBuilding: false,
      localUserSettings: null,
      isLoading: true,
      rawDataContext: [],
      tableDataSource: [],
      tableIsRendering: false,
      /** <--- Background data ---> */
      locations: [],
      fractions: [],
      suppliers: [],
      infocards: [],
      ewcs: [],
      ghgCodes: [],
      containerTypes: [],
      containerTypeAndSizes: [],
      receivingFacilities: [],
      wasteGroups: [],
      wasteCategoryOptions: [
        {
          id: 'Unknown',
          name: this.T('Web.Generic.WasteCategories.Unknown'),
        },
        {
          id: 'Rezycl',
          name: this.T('Web.Generic.WasteCategories.Rezycl'),
        },
        {
          id: 'Combostion',
          name: this.T('Web.Generic.WasteCategories.Combostion'),
        },
        {
          id: 'Special',
          name: this.T('Web.Generic.WasteCategories.Special'),
        },
        {
          id: 'Landfill',
          name: this.T('Web.Generic.WasteCategories.Landfill'),
        },
        {
          id: 'Reuse',
          name: this.T('Web.Generic.WasteCategories.Reuse'),
        },
      ],
    }
  },

  computed: {
    /** <--- Filter & group options ---> */
    columnOptions() {
      return [
        {
          key: 'year',
          label: this.T(
            'Web.DataInsightsPage.Options.AnnualDistribution',
            'Annual distribution',
          ),
        },
        {
          key: 'month',
          label: this.T(
            'Web.DataInsightsPage.Options.MonthlyDistribution',
            'Monthly distribution',
          ),
        },
        {
          key: 'locationId',
          label: this.T('Web.DataInsightsPage.Options.Location', 'Location'),
        },
        {
          key: 'vocherNumber',
          label: this.T(
            'Web.DataInsightsPage.Options.VocherNumber',
            'Vocher number',
          ),
        },
        {
          key: 'infoCardId',
          label: this.T('Web.DataInsightsPage.Options.Infocard', 'Infocard'),
        },
        {
          key: 'wasteCategory',
          label: this.T(
            'Web.DataInsightsPage.Options.WasteCategory',
            'Waste category',
          ),
        },
        {
          key: 'ewcCodeName',
          label: this.T('Web.DataInsightsPage.Options.EwcCode', 'EWC code'),
        },
        {
          key: 'ghgCodeId',
          label: this.T('Web.DataInsightsPage.Options.GHGCode', 'GHG code'),
        },
        {
          key: 'wasteGroupId',
          label: this.T('Web.DataInsightsPage.Options.WasteGroup', 'Affaldsgruppe'),
        },
      ]
    },
    rowOptions() {
      return [
        {
          key: 'locationId',
          label: this.T('Web.DataInsightsPage.Options.Location', 'Location'),
          value: this.locations,
        },
        {
          key: 'wasteFractionId',
          label: this.T(
            'Web.DataInsightsPage.Options.WasteFraction',
            'Fraction',
          ),
          value: [
            ...this.fractions,
            ...[
              {
                id: null,
                name: this.T(
                  'Web.DataInsightsPage.Options.WithoutWasteFraction',
                  'Without fraction',
                ),
              },
            ],
          ],
        },
        {
          key: 'supplierId',
          label: this.T('Web.DataInsightsPage.Options.Supplier', 'Supplier'),
          value: [
            ...this.suppliers,
            ...[
              {
                id: null,
                name: this.T(
                  'Web.DataInsightsPage.Options.WithoutSupplier',
                  'Without supplier',
                ),
              },
            ],
          ],
        },
        /*
        {
          key: "infoCardId",
          label: this.T("Web.DataInsightsPage.Options.Infocard", "Infocard"),
          value: this.infocards,
        },
*/
        {
          key: 'infocardInternalId',
          label: this.T(
            'Web.DataInsightsPage.Options.InfocardInternalId',
            'Infocard internal group id',
          ),
          value: [
            ...this.infocards.reduce((prev, curr) => {
              if (
                curr.internalGroupId != null
                && curr.internalGroupId != ''
                && !prev.some(i => i.id == curr.internalGroupId)
              ) {
                prev.push({
                  id: curr.internalGroupId,
                  name: curr.internalGroupId,
                })
              }
              return prev
            }, []),
            ...[
              {
                id: null,
                name: this.T(
                  'Web.DataInsightsPage.Options.WithoutInfocardInternalId',
                  'Without id',
                ),
              },
            ],
          ],
        },
        {
          key: 'wasteCategory',
          label: this.T(
            'Web.DataInsightsPage.Options.WasteCategory',
            'Waste category',
          ),
          value: this.wasteCategoryOptions,
        },
        {
          key: 'ewcCodeName',
          label: this.T('Web.DataInsightsPage.Options.EwcCode', 'EWC code'),
          value: [
            ...this.ewcs,
            ...[
              {
                id: null,
                name: this.T(
                  'Web.DataInsightsPage.Options.WithoutEwcCode',
                  'Without EWC code',
                ),
              },
            ],
          ],
        },
        {
          key: 'ghgCodeId',
          label: this.T('Web.DataInsightsPage.Options.GHGCode', 'GHG code'),
          value: [
            ...this.ghgCodes,
            ...[
              {
                id: null,
                name: this.T(
                  'Web.DataInsightsPage.Options.WithoutGHGCode',
                  'Without GHG code',
                ),
              },
            ],
          ],
        },
        {
          key: 'containerTypeId',
          label: this.T(
            'Web.DataInsightsPage.Options.ContainerType',
            'Container type',
          ),
          value: [
            ...this.containerTypes,
            ...[
              {
                id: null,
                name: this.T(
                  'Web.DataInsightsPage.Options.WithoutContainerType',
                  'No type defined',
                ),
              },
            ],
          ],
        },
        {
          key: 'containerTypeAndSize',
          label: this.T(
            'Web.DataInsightsPage.Options.ContainerTypeAndSize',
            'Container type & size',
          ),
          value: [
            ...this.containerTypeAndSizes,
            ...[
              {
                id: null,
                name: this.T(
                  'Web.DataInsightsPage.Options.WithoutContainerTypeAndSize',
                  'No type defined',
                ),
              },
            ],
          ],
        },
        {
          key: 'receivingFacilityId',
          label: this.T(
            'Web.DataInsightsPage.Options.RecivingFacility',
            'Reciving facility',
          ),
          value: [
            ...this.receivingFacilities,
            ...[
              {
                id: null,
                name: this.T(
                  'Web.DataInsightsPage.Options.WithoutRecivingFacility',
                  'Without reciving facility',
                ),
              },
            ],
          ],
        },
        {
          key: 'wasteGroupId',
          label: this.T(
            'Web.DataInsightsPage.Options.WasteGroup',
            'Affaldsgruppe',
          ),
          value: [
            ...this.wasteGroups,
            ...[
              {
                id: null,
                name: this.T(
                  'Web.DataInsightsPage.Options.WithoutWasteGroup',
                  'Without affaldsgruppe',
                ),
              },
            ],
          ],
        },
      ]
    },
    valuesOptions() {
      const self = this
      return [
        {
          key: 'tonnageWasteCollected',
          label: this.T(
            'Web.DataInsightsPage.Options.TonnageCollected',
            'Tonnage collected',
          ),
          formatter(value) {
            return self.convertTo3decimals(value)
          },
        },
        {
          key: 'wasteCollectionCount',
          label: this.T(
            'Web.DataInsightsPage.Options.CollectionCount',
            'Number of collections',
          ),
        },
        {
          key: 'wasteCollectionTotalPrice',
          label: this.T(
            'Web.DataInsightsPage.Options.CollectionTotalPrice',
            'Collections total price',
          ),
        },
        {
          key: 'wasteCollectionPricePrTonnage',
          label: this.T(
            'Web.DataInsightsPage.Options.AverageCollectionPricePerTon',
            'Average collection price per ton',
          ),
          value_resolver: items => {
            const wasteCollectionTotalPrice = items.reduce((prev, curr) => {
              prev += curr.wasteCollectionTotalPrice
              return prev
            }, 0)
            const totalTonsCollected = items.reduce((prev, curr) => {
              prev += curr.tonnageWasteCollected
              return prev
            }, 0)
            return totalTonsCollected <= 0
              ? 0
              : wasteCollectionTotalPrice / totalTonsCollected
          },
        },
        {
          key: 'wasteCollectionAvgUnitPrice',
          label: this.T(
            'Web.DataInsightsPage.Options.AverageCollectionUnitPrice',
            'Average collection unit price',
          ),
          value_resolver: items => {
            const totalUnitPrice = items.reduce((prev, curr) => {
              prev += curr.wasteCollectionUnitPrice
              return prev
            }, 0)
            const unitPricesAmount = items.reduce((prev, curr) => {
              prev += curr.wasteCollectionUnitPrice != null ? 1 : 0
              return prev
            }, 0)
            return unitPricesAmount > 0 ? totalUnitPrice / unitPricesAmount : 0
          },
        },
        {
          key: 'wasteCollectionPricePrCollection',
          label: this.T(
            'Web.DataInsightsPage.Options.AveragePricePrCollection',
            'Average price per collection',
          ),
          value_resolver: items => {
            const wasteCollectionTotalPrice = items.reduce((prev, curr) => {
              prev += curr.wasteCollectionTotalPrice
              return prev
            }, 0)
            const totalCollections = items.reduce((prev, curr) => {
              prev += curr.wasteCollectionCount
              return prev
            }, 0)
            return totalCollections <= 0
              ? 0
              : wasteCollectionTotalPrice / totalCollections
          },
        },
        {
          key: 'incomeTotalPrice',
          label: this.T(
            'Web.DataInsightsPage.Options.GrossIncome',
            'Gross income',
          ),
        },
        {
          key: 'incomeTotalNettoPrice',
          label: this.T('Web.DataInsightsPage.Options.NetIncome', 'Net income'),
          value_resolver: items => {
            const incomeTotal = items.reduce((prev, curr) => {
              prev += curr.incomeTotalPrice
              return prev
            }, 0)
            const chargeTotal = items.reduce((prev, curr) => {
              prev += curr.chageTotalPrice
              return prev
            }, 0)

            return incomeTotal - chargeTotal
          },
        },
        {
          key: 'incomeAvgUnitPrice',
          label: this.T(
            'Web.DataInsightsPage.Options.IncomeAverageUnitPrice',
            'Average unit price of income',
          ),
          value_resolver: items => {
            const totalUnitPrice = items.reduce((prev, curr) => {
              prev += curr.incomeUnitPrice
              return prev
            }, 0)
            const unitPricesAmount = items.reduce((prev, curr) => {
              prev += curr.incomeUnitPrice != null ? 1 : 0
              return prev
            }, 0)
            return unitPricesAmount > 0 ? totalUnitPrice / unitPricesAmount : 0
          },
        },
        {
          key: 'incomeAvgPricePrTonnage',
          label: this.T(
            'Web.DataInsightsPage.Options.AverageIncomePerTon',
            'Average income per ton',
          ),
          value_resolver: items => {
            const incomeTotal = items.reduce((prev, curr) => {
              prev += curr.incomeTotalPrice
              return prev
            }, 0)
            const totalTonsCollected = items.reduce((prev, curr) => {
              prev += curr.tonnageWasteCollected
              return prev
            }, 0)
            return totalTonsCollected <= 0
              ? 0
              : incomeTotal / totalTonsCollected
          },
        },
        {
          key: 'chageTotalPrice',
          label: this.T(
            'Web.DataInsightsPage.Options.GrossCharge',
            'Gross charge',
          ),
        },
        {
          key: 'chageTotalNettoPrice',
          label: this.T('Web.DataInsightsPage.Options.NetCharge', 'Net charge'),
          value_resolver: items => {
            const incomeTotal = items.reduce((prev, curr) => {
              prev += curr.incomeTotalPrice
              return prev
            }, 0)
            const chargeTotal = items.reduce((prev, curr) => {
              prev += curr.chageTotalPrice
              return prev
            }, 0)

            return chargeTotal - incomeTotal
          },
        },
        {
          key: 'chageAvgUnitPrice',
          label: this.T(
            'Web.DataInsightsPage.Options.ChargeAverageUnitPrice',
            'Charge average unit price',
          ),
          value_resolver: items => {
            const totalUnitPrice = items.reduce((prev, curr) => {
              prev += curr.chageUnitPrice
              return prev
            }, 0)
            const unitPricesAmount = items.reduce((prev, curr) => {
              prev += curr.chageUnitPrice != null ? 1 : 0
              return prev
            }, 0)
            return unitPricesAmount > 0 ? totalUnitPrice / unitPricesAmount : 0
          },
        },
        {
          key: 'chargeAvgPricePrTonnage',
          label: this.T(
            'Web.DataInsightsPage.Options.ChargeAveragePerTon',
            'Charge average per ton',
          ),
          value_resolver: items => {
            const incomeTotal = items.reduce((prev, curr) => {
              prev += curr.chageTotalPrice
              return prev
            }, 0)
            const totalTonsCollected = items.reduce((prev, curr) => {
              prev += curr.tonnageWasteCollected
              return prev
            }, 0)
            return totalTonsCollected <= 0
              ? 0
              : incomeTotal / totalTonsCollected
          },
        },
        {
          key: 'abolitionCO2',
          label: this.T(
            'Web.DataInsightsPage.Options.CO2EmissionsFromDisposal',
            'CO2 emissions from disposal',
          ),
        },
        {
          key: 'transportCO2',
          label: this.T(
            'Web.DataInsightsPage.Options.CO2EmissionsFromTransportation',
            'CO2 emissions from transportation',
          ),
        },
        {
          key: 'kmTransported',
          label: this.T(
            'Web.DataInsightsPage.Options.KilometersTransported',
            'Kilometers transported',
          ),
        },
        {
          key: 'genuineRecyclingTonnageWasteCollected',
          label: 'Reel genanvendt (Tons)',
          formatter(value) {
            return self.convertTo3decimals(value)
          },
        },
        {
          key: 'tonnageWasteWrongSorted',
          label: 'Fejlsorteret (Tons)',
          formatter(value) {
            return self.convertTo3decimals(value)
          },
        },
      ]
    },
    visibleColumnOptions() {
      const { visibleOptions } = this.localUserSettings
      return this.columnOptions.filter(o => visibleOptions.columnOptions.includes(o.key))
    },
    visibleRowOptions() {
      const { visibleOptions } = this.localUserSettings
      return this.rowOptions.filter(o => visibleOptions.rowOptions.includes(o.key))
    },
    visibleValuesOptions() {
      const { visibleOptions } = this.localUserSettings
      return this.valuesOptions.filter(o => visibleOptions.valuesOptions.includes(o.key))
    },
    visibleFilterOptions() {
      return this.rowOptions.filter(
        o => this.selectedRowOptions.some(r => r.key == o.key)
          || this.selectedColumnOption == o.key,
      )
    },
    selectedColumnOption() {
      return this.localUserSettings.selectedColumnOption
    },

    /** <--- Column, rows & data ---> */
    columns() {
      if (this.localUserSettings.selectedColumnOption == null) return []
      switch (this.localUserSettings.selectedColumnOption) {
        case 'year':
          return this.getYearsInPeriod(
            new Date(this.localUserSettings.dateRange.startDate),
            new Date(this.localUserSettings.dateRange.endDate),
          ).map(year => ({ label: year, value: year }))

        case 'month':
          return this.getMonthsInPeriod(
            new Date(this.localUserSettings.dateRange.startDate),
            new Date(this.localUserSettings.dateRange.endDate),
          ).map(month => ({
            label: `${month.monthName} ${month.year}`,
            value: `${month.month}-${month.year}`,
          }))
        case 'locationId':
          return this.locations
            .filter(i => this.filters.locationId.includes(i.id))
            .map(location => ({
              label: location.name,
              value: location.id,
            }))
        case 'vocherNumber':
          return this.filteredRawDataContext.reduce((prev, curr) => {
            if (!prev.some(i => i.label == curr.vocherNumber)) prev.push({ label: curr.vocherNumber, value: curr.vocherNumber })
            return prev
          }, [])
        case 'infoCardId':
          return this.infocards.map(infocard => ({
            label: infocard.name,
            value: infocard.id,
          }))
        case 'wasteCategory':
          return this.wasteCategoryOptions.map(option => ({
            label: option.name,
            value: option.id,
          }))
        case 'ewcCodeName':
          return [
            ...this.ewcs.map(option => ({
              label: option.name,
              value: option.id,
            })),
            ...[
              {
                label: this.T('Web.DataInsightsPage.Options.WithoutEwcCode'),
                value: null,
              },
            ],
          ]
        case 'ghgCodeId':
          return [
            ...this.ghgCodes.map(option => ({
              label: option.name,
              value: option.id,
            })),
            ...[
              {
                label: this.T('Web.DataInsightsPage.Options.WithoutGHGCode'),
                value: null,
              },
            ],
          ]
        case 'wasteGroupId':
          return [
            ...this.wasteGroups.map(option => ({
              label: option.name,
              value: option.id,
            })),
            ...[
              {
                label: this.T('Web.DataInsightsPage.Options.WithoutWasteGroup', 'Without affaldsgruppe'),
                value: null,
              },
            ],
          ]
      }
    },
    canShowTableResults() {
      return (
        this.localUserSettings != null
        && this.selectedRowOptions.length > 0
        && this.selectedValuesOptions.length > 0
      )
    },
    tableData() {
      this.tableIsBuilding = true
      const data = {
        columns: this.columns,
        data: this.tableDataSource, // this.buildTableData(this.filteredRawDataContext, this.sortOption),
      }
      this.tableIsBuilding = false
      return data
    },
    filteredRawDataContext() {
      if (this.rawDataContext.length == 0) return []
      let filteredResult = this.rawDataContext
      this.visibleFilterOptions.forEach(filterOption => {
        const filtersPropertyValues = this.rowOptions
          .find(o => o.key == filterOption.key)
          .value.map(i => i.id)
        filteredResult = filteredResult.filter(i => filtersPropertyValues.includes(i[filterOption.key]))
      })
      return filteredResult
    },
    tableRows() {
      return this.getTableRowsForLevel()
    },
    /** Pivo settings */
    userSettings: {
      async get() {
        return await this.getUserSetting(
          'data.insights.pivo.settings',
          this.defaultUserSettings,
        )
      },
      async set(value) {
        await this.setUserSettingAsync('data.insights.pivo.settings', value)
      },
    },
    defaultUserSettings() {
      const filters = {}
      this.rowOptions.forEach(option => {
        filters[option.key] = option.value.map(i => i.id)
      })
      return {
        dateRange: {
          startDate: moment(new Date()).subtract('year', 1),
          endDate: moment(new Date()),
        },
        selectedColumnOption: 'month',
        selectedRowOptions: [],
        selectedValuesOptions: [],
        filters,
        visibleOptions: {
          valuesOptions: [
            'tonnageWasteCollected',
            'wasteCollectionCount',
            'wasteCollectionTotalPrice',
            'incomeTotalPrice',
            'chageTotalPrice',
          ],
          columnOptions: ['year', 'month'],
          rowOptions: [
            'locationId',
            'wasteFractionId',
            'supplierId',
            'infoCardId',
            'wasteCategory',
          ],
        },
        sortOption: null,
      }
    },

    filters: {
      get() {
        return this.localUserSettings.filters
      },
      set(value) {
        this.localUserSettings.filters = value
      },
    },
    selectedValuesOptions() {
      return this.localUserSettings.selectedValuesOptions.map(optionKey => this.valuesOptions.find(o => o.key == optionKey))
    },
    selectedRowOptions() {
      return this.localUserSettings.selectedRowOptions.map(optionKey => this.rowOptions.find(o => o.key == optionKey))
    },
    sortOption() {
      return this.localUserSettings.sortOption
    },
  },
  watch: {
    localUserSettings: {
      handler(newValue, oldValue) {
        if (oldValue == null) return
        this.userSettings = newValue
      },
      deep: true,
    },
  },
  async mounted() {
    this.isLoading = true
    const containerElm = document.getElementById('data-insights-con')
    const elementRect = containerElm.getBoundingClientRect()
    const elementTop = elementRect.top + window.scrollY
    containerElm.style.height = `calc(100vh - ${elementTop}px)`

    // Define services
    const locationsService = new ResourceService('background-data/locations')
    const fractionsService = new ResourceService('waste-fractions')
    const suppliersService = new ResourceService('supplier-contacts')
    const infocardService = new ResourceService('background-data/infocards')
    const ewcServices = new ResourceService('ewcs')
    const ghgCodeService = new ResourceService('background-data/ghg-codes')
    const containerTypeService = new ResourceService(
      'info-cards/containers/types',
    )
    const receivingFacilityService = new ResourceService(
      'receiving-facilities',
    )
    const wasteGroupService = new ResourceService('waste-fractions')

    this.localUserSettings = await this.userSettings

    const infocardIdRowExists = this.localUserSettings.selectedRowOptions.find(
      o => o == 'infoCardId',
    )
    if (infocardIdRowExists) {
      this.localUserSettings.selectedRowOptions.splice(
        this.localUserSettings.selectedRowOptions.indexOf(infocardIdRowExists),
        1,
      )
    }

    // Fetch all data concurrently
    const fetchAllData = async () => {
      const [
        dataInsightData,
        locations,
        fractions,
        suppliers,
        infocards,
        ewcs,
        ghgCodes,
        containerTypes,
        receivingFacilities,
        wasteGroups,
      ] = await Promise.all([
        this.fetchDataPoints(false),
        locationsService.get('').then(res => res.data.data),
        fractionsService.get('').then(res => res.data.data),
        suppliersService.get('').then(res => res.data.data),
        infocardService.get('').then(res => res.data.data),
        ewcServices.get('').then(res => res.data.data),
        ghgCodeService.get('').then(res => res.data.data),
        containerTypeService.get('').then(res => res.data.data),
        receivingFacilityService.get('').then(res => res.data.data),
        wasteGroupService.get('groups').then(res => res.data.data),
      ])

      // Transformations
      const ewcFiltered = ewcs
        .filter(
          e => e.alias != null || infocards.some(i => i.ewcCodeName === e.code),
        )
        .map(e => ({
          id: e.code,
          name: e.code,
          description: e.name,
        }))

      const containerTypeAndSizes = infocards.reduce((prev, curr) => {
        if (
          curr.containerTypeName != null
          && curr.size > 0
          && curr.sizeUnit !== 'Unknown'
        ) {
          prev.push({
            id: `${curr.containerTypeId}_${curr.size}_${curr.sizeUnit}`,
            name: `${curr.containerTypeName} ${curr.size} ${
              this.getContainerSizeUnit(curr.sizeUnit).text
            }`,
          })
        }
        return prev
      }, [])

      // Assign results to instance properties
      this.locations = locations
      this.fractions = fractions
      this.suppliers = suppliers
      this.infocards = infocards
      this.containerTypes = containerTypes
      this.receivingFacilities = receivingFacilities
      this.ewcs = ewcFiltered
      this.ghgCodes = ghgCodes
      this.containerTypeAndSizes = containerTypeAndSizes
      this.wasteGroups = wasteGroups

      this.enrichDataPointsWithWasteGroup()

      // Build table
      this.tableDataSource = this.buildTableData(
        this.filteredRawDataContext,
        this.sortOption,
      )
    }

    // Execute the function
    fetchAllData()
      .catch(error => {
        console.error('Failed to fetch data:', error)
      })
      .then(() => {
        this.isLoading = false
      })
  },
  methods: {
    async handleDateRangeChange() {
      this.tableIsBuilding = true
      await this.fetchDataPoints()
      this.enrichDataPointsWithWasteGroup()
      this.refreshTable()
    },

    refreshTable() {
      this.tableIsBuilding = true
      this.tableIsRendering = true
      setTimeout(() => {
        this.tableDataSource = this.buildTableData(
          this.filteredRawDataContext,
          this.sortOption,
        )
        this.tableIsBuilding = false
      }, 0)
    },
    buildTableData(dataContext, sortOption, optionIndex = 0) {
      if (this.selectedRowOptions.length === 0) return []

      const result = []
      const selectedRowOption = this.selectedRowOptions[optionIndex]
      const selectedColumnOption = this.columnOptions.find(
        o => o.key === this.localUserSettings.selectedColumnOption,
      )

      // Precompute filters for row options
      const filteredRowValues = selectedRowOption.value.filter(i => this.filters[selectedRowOption.key].includes(i.id))

      filteredRowValues.forEach(elm => {
        // Filter data once for the current row option
        const filteredDataContext = dataContext.filter(
          c => c[selectedRowOption.key] === elm.id,
        )

        // Precompute column data
        const rowColumnsData = this.columns.map(column => {
          const columnKey = column.value
          return {
            key: columnKey,
            values: this.selectedValuesOptions.map(option => {
              const resolver = option.value_resolver
              const formatter = option.formatter || this.$options.filters.toCurrencyRounded

              // Filter once per column
              const valueItems = filteredDataContext.filter(
                d => d[selectedColumnOption.key] === columnKey,
              )

              const value = resolver
                ? resolver(valueItems)
                : valueItems.reduce((prev, curr) => prev + curr[option.key], 0)

              const displayValue = valueItems.length === 0 || isNaN(value)
                ? '-'
                : formatter(value)

              return {
                label: option.label,
                value,
                displayValue,
              }
            }),
          }
        })

        // Compute row total data
        const rowTotalValues = this.selectedValuesOptions.map(option => {
          const totalValue = rowColumnsData.reduce((prev, curr) => {
            const rowValueToAdd = curr.values.find(
              o => o.label === option.label,
            ).value
            return prev + rowValueToAdd
          }, 0)

          return {
            key: option.key,
            label: option.label,
            value: totalValue,
            displayValue: option.formatter
              ? option.formatter(totalValue)
              : this.$options.filters.toCurrencyRounded(totalValue),
          }
        })

        // Construct result item
        const resultItem = {
          rowId: uuid.v4(),
          label: elm.name,
          id: elm.id,
          children: [],
          rowData: filteredDataContext,
          rowColumnsData,
          rowTotals: rowTotalValues,
        }

        // Recursively build child data if necessary
        if (this.selectedRowOptions.length > optionIndex + 1) {
          resultItem.children = this.buildTableData(
            filteredDataContext,
            sortOption,
            optionIndex + 1,
          )

          // If children exist, recalculate parent totals based on children's totals
          if (resultItem.children.length > 0) {
            // Recalculate row totals from children
            const recalculatedRowTotals = this.selectedValuesOptions.map(option => {
              const totalValue = resultItem.children.reduce((prev, child) => {
                const childTotal = child.rowTotals.find(t => t.label === option.label)
                return prev + (childTotal ? childTotal.value : 0)
              }, 0)

              return {
                key: option.key,
                label: option.label,
                value: totalValue,
                displayValue: option.formatter
                  ? option.formatter(totalValue)
                  : this.$options.filters.toCurrencyRounded(totalValue),
              }
            })
            resultItem.rowTotals = recalculatedRowTotals

            // Also recalculate column data from children
            const recalculatedColumnsData = resultItem.rowColumnsData.map((col, colIndex) => ({
              ...col,
              values: col.values.map((val, valIndex) => {
                const totalValue = resultItem.children.reduce((prev, child) => {
                  const childCol = child.rowColumnsData[colIndex]
                  if (childCol && childCol.values[valIndex]) {
                    return prev + childCol.values[valIndex].value
                  }
                  return prev
                }, 0)

                const option = this.selectedValuesOptions[valIndex]
                const formatter = option.formatter || this.$options.filters.toCurrencyRounded

                return {
                  label: val.label,
                  value: totalValue,
                  displayValue: totalValue === 0 || isNaN(totalValue) ? '-' : formatter(totalValue),
                }
              }),
            }))
            resultItem.rowColumnsData = recalculatedColumnsData
          }
        }

        result.push(resultItem)
      })

      // Sort results
      if (sortOption) {
        result.sort((a, b) => {
          const valueA = a.rowTotals.find(r => r.key === sortOption.columnKey)?.value || 0
          const valueB = b.rowTotals.find(r => r.key === sortOption.columnKey)?.value || 0
          return sortOption.direction === 'DESC'
            ? valueB - valueA
            : valueA - valueB
        })
      } else {
        result.sort((a, b) => a.label.localeCompare(b.label))
      }

      return result
    },

    buildTableData1(dataContext, sortOption, optionIndex = 0) {
      if (this.selectedRowOptions.length == 0) return []
      let result = []
      const selectedRowOption = this.selectedRowOptions[optionIndex]
      const selectedColumnOption = this.columnOptions.find(
        o => o.key == this.localUserSettings.selectedColumnOption,
      )
      selectedRowOption.value
        .filter(i => this.filters[selectedRowOption.key].includes(i.id))
        .forEach(elm => {
          const filteredDataContext = dataContext.filter(c => c[selectedRowOption.key] == elm.id)
          /** Row data */
          const rowColumnsData = this.columns.map(column => ({
            key: column.value,
            values: this.selectedValuesOptions.map(option => {
              const valueItems = filteredDataContext.filter(
                d => d[selectedColumnOption.key] == column.value,
              )
              let value = 0
              if (option.value_resolver != undefined) {
                value = option.value_resolver(valueItems)
              } else {
                value = valueItems.reduce(
                  (prev, curr) => (prev += curr[option.key]),
                  0,
                )
              }
              const displayValue = option.formatter
                ? option.formatter(value)
                : this.$options.filters.toCurrencyRounded(value)
                // No data present
              if (valueItems.length == 0) {
              }
              return {
                label: option.label,
                value,
                displayValue:
                    valueItems.length == 0 || displayValue == 'NaN'
                      ? '-'
                      : displayValue,
              }
            }),
          }))
          /** Row total data */
          const rowTotalValues = this.selectedValuesOptions.map(option => {
            const totalValue = rowColumnsData.reduce((prev, curr) => {
              const rowValueToAdd = curr.values.find(
                o => o.label == option.label,
              ).value
              return (prev += rowValueToAdd)
            }, 0)
            return {
              key: option.key,
              label: option.label,
              value: totalValue,
              displayValue: option.formatter
                ? option.formatter(totalValue)
                : this.$options.filters.toCurrencyRounded(totalValue),
            }
          })

          const resultItem = {
            rowId: uuid.v4(),
            label: elm.name,
            id: elm.id,
            children: [],
            rowData: filteredDataContext,
            rowColumnsData,
            rowTotals: rowTotalValues,
          }

          if (this.selectedRowOptions.length > optionIndex + 1) {
            resultItem.children = this.buildTableData(
              filteredDataContext,
              sortOption,
              optionIndex + 1,
            )
          }

          result.push(resultItem)
        })
      if (this.sortOption != null) {
        result = result.sort((a, b) => {
          const valueA = a.rowTotals.find(
            r => r.key == sortOption.columnKey,
          )?.value
          const valueB = b.rowTotals.find(
            r => r.key == sortOption.columnKey,
          )?.value
          return sortOption.direction == 'DESC'
            ? valueA - valueB
            : valueB - valueA
        })
      } else {
        result = result.sort((a, b) => a.label?.localeCompare(b.label))
      }
      return result
    },
    getYearsInPeriod(startDate, endDate) {
      const years = []
      const currentDate = moment(startDate).clone()
      while (currentDate.isSameOrBefore(moment(endDate), 'year')) {
        years.push(currentDate.year())
        currentDate.add(1, 'year')
      }
      return years
    },
    getMonthsInPeriod(startDate, endDate) {
      const months = []
      const currentDate = moment(startDate).clone()

      while (currentDate.isSameOrBefore(moment(endDate), 'month')) {
        months.push({
          monthName: currentDate.format('MMM'),
          month: currentDate.format('MM'),
          year: currentDate.format('YYYY'),
        })
        currentDate.add(1, 'month')
      }
      return months
    },
    getTableRowsForLevel(level = 0) {
      if (this.selectedRowOptions.length == 0) return []
      let result = []
      this.selectedRowOptions[level].value.forEach(element => {
        result.push({
          level,
          element,
        })
        if (this.selectedRowOptions.length > level + 1) {
          result = result.concat(this.getTableRowsForLevel(level + 1))
        }
      })
      return result
    },
    selectDeselectAllFilterOption(optionKey) {
      if (
        this.filters[optionKey].length
        == this.rowOptions.find(o => o.key == optionKey).value.length
      ) {
        this.filters[optionKey] = []
      } else {
        this.filters[optionKey] = this.rowOptions
          .find(o => o.key == optionKey)
          .value.map(v => v.id)
      }
      this.refreshTable()
    },
    openValueOptionsVisibilityEditModal() {
      this.$refs.valueOptionsVisibilityEditModal.open()
    },
    openColumnOptionsVisibilityEditModal() {
      this.$refs.columnOptionsVisibilityEditModal.open()
    },
    openRowOptionsVisibilityEditModal() {
      this.$refs.rowOptionsVisibilityEditModal.open()
    },
    async fetchDataPoints(setLoading = true) {
      try {
        const service = new ResourceService('data-insights')
        const { data } = await service.get('data-points', {
          fromDate: moment(this.localUserSettings.dateRange.startDate).format(
            'yyyy-MM-DD',
          ),
          toDate: moment(this.localUserSettings.dateRange.endDate).format(
            'yyyy-MM-DD',
          ),
        })
        this.rawDataContext = data.data
        if (setLoading) {
          this.isLoading = true
        }
      } finally {
        if (setLoading) {
          this.isLoading = false
        }
      }
    },
    enrichDataPointsWithWasteGroup() {
      const fractionToWasteGroup = this.fractions.reduce((map, f) => {
        if (f.wasteGroupId) map[f.id] = f.wasteGroupId
        return map
      }, {})
      this.rawDataContext.forEach(dp => {
        dp.wasteGroupId = fractionToWasteGroup[dp.wasteFractionId] || null
      })
    },
    exportToExcel() {
      const startDate = moment(
        this.localUserSettings.dateRange.startDate,
      ).format('YYYY-MM-DD')
      const endDate = moment(this.localUserSettings.dateRange.endDate).format(
        'YYYY-MM-DD',
      )
      // Prepare data for export
      const exportData = []

      // Create header row
      const headerRow = {
        [`${startDate} > ${endDate}`]: 'Total',
      }

      // Add column headers
      this.tableData.columns.forEach(column => {
        this.selectedValuesOptions.forEach(valueOption => {
          const columnHeader = `${column.label} - ${valueOption.label}`
          headerRow[columnHeader] = ''
        })
      })
      // Add total values to header row
      const totalRowsValues = []
      this.tableData.columns.forEach(col => {
        this.selectedValuesOptions.forEach((option, index) => {
          const value = this.tableData.data.reduce(
            (prev, curr) => (prev += curr.rowColumnsData.find(i => i.key == col.value)
              .values[index].value),
            0,
          )
          totalRowsValues.push({
            value,
            displayValue: option.formatter
              ? option.formatter(value)
              : this.$options.filters.toCurrency(value),
          })
        })
      })

      let colIndex = 0
      this.tableData.columns.forEach(column => {
        this.selectedValuesOptions.forEach(valueOption => {
          const columnHeader = `${column.label} - ${valueOption.label}`
          const rowTotal = totalRowsValues[colIndex]
          if (rowTotal) {
            headerRow[columnHeader] = rowTotal.displayValue
          } else {
            headerRow[columnHeader] = '--'
          }
          colIndex++
        })
      })

      exportData.push(headerRow)

      // Function to process rows recursively
      const processRows = (rows, level = 0) => {
        rows.forEach(row => {
          const rowData = {
            [`${startDate} > ${endDate}`]: '  '.repeat(level) + row.label,
          }

          // Add row total values
          this.selectedValuesOptions.forEach((valueOption, index) => {
            const totalColumnHeader = `Total - ${valueOption.label}`
            if (!headerRow[totalColumnHeader]) {
              headerRow[totalColumnHeader] = ''
            }
            rowData[totalColumnHeader] = row.rowTotals[index].displayValue
          })

          // Add column values
          row.rowColumnsData.forEach(columnData => {
            const column = this.tableData.columns.find(
              col => col.value === columnData.key,
            )
            columnData.values.forEach(value => {
              const columnHeader = `${column.label} - ${value.label}`
              rowData[columnHeader] = value.displayValue
            })
          })

          exportData.push(rowData)

          // Process children if any
          if (row.children && row.children.length > 0) {
            processRows(row.children, level + 1)
          }
        })
      }

      // Process all rows
      processRows(this.tableData.data)

      // Create worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData)

      // Create workbook
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data Insights')

      // Generate filename with date range

      const filename = `DataInsights_${startDate}_to_${endDate}.xlsx`

      // Write file
      XLSX.writeFile(workbook, filename)
    },
  },
}
</script>
<style  >
.sortable-ghost,
.sortable-chosen {
  background: #e3ebe1;
}
</style>
<style lang="scss" scoped>
#data-insights-con {
  display: flex;
  height: 100vh;
  padding: 0 0 20px 0;
  .left-area {
    width: 80%;
    margin-right: 10px;
    height: 100%;

    .card {
      height: 100%;
      .info-text-con {
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
  .right-area {
    width: 20%;
    height: 100%;
    .card {
      height: 100%;
      padding: 0px 0px 20px 0px;
      display: flex;
      align-items: stretch;

      .content-inner {
        height: 100%;
        overflow-y: auto;

        .editOptionsBtn {
          font-weight: 500;
          font-size: 12px;
          margin: 5px 0px;
          svg {
            margin-right: 10px;
          }
        }
      }

      .data-insights-brand-con {
        background-color: #e3ebe1;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #407e3d;
        font-size: 20px;
        font-weight: 300;
        padding: 10px 0px;
        position: sticky;
        top: 0;
        border-bottom: 2px solid #407e3d;
      }
      .content-inner {
        padding: 1.5rem;
      }
    }
  }
}

table {
  th,
  td {
    border: 1px solid;
  }
}

.order-editor-con {
  margin-top: 10px;
  label {
    font-weight: 400;
  }
  .order-editor {
    border-radius: 4px;
    border: 1px solid #e4e4e4;
    background: white;

    > div > div {
      padding: 5px 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      &:not(:last-child) {
        border-bottom: 1px solid #e4e4e4;
      }

      &:hover {
        cursor: ns-resize;
      }

      .icon-controls {
        display: flex;
        flex-direction: column;
      }
    }
  }
}

.filter-editor-con {
  margin-top: 10px;

  label {
    font-weight: 400;
  }
  .filter-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 500;
    font-size: 12px;
  }
  .filter-editor {
    border-radius: 4px;
    border: 1px solid #e4e4e4;
    background: white;

    > div {
      padding: 5px 10px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      &:not(:last-child) {
        border-bottom: 1px solid #e4e4e4;
      }
    }
  }
}
</style>

<style scoped>
/* Skeleton Loader Animation */
@keyframes skeleton-loading {
  0% {
    background-color: #e0e0e0;
  }
  50% {
    background-color: #f0f0f0;
  }
  100% {
    background-color: #e0e0e0;
  }
}

/* Full-page skeleton container */
.page-skeleton {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f7f7f7;
}

/* Container for the two boxes */
.skeleton-container {
  height: 100%;
  display: flex;
  width: 100%;
  justify-content: space-between;
  gap: 10px;
  margin-right: 20px;
}

/* Common styles for both boxes */
.skeleton-box {
  height: 100%;
  border-radius: 0.428rem;
  animation: skeleton-loading 1.5s infinite ease-in-out;
  padding: 20px;
}

/* Large box (80% width) */
.skeleton-box.large {
  flex: 0 0 80%;
  height: 100%;
}

/* Small box (20% width) */
.skeleton-box.small {
  flex: 0 0 20%;
  height: 100%;
}
.skeleton-text {
  font-size: 24px;
}

.table-header-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}
</style>
